import {useContext, useEffect, useRef} from 'react';
import type {LoaderFunctionArgs} from 'react-router';
import {matchRoutes, UNSAFE_DataRouterContext, useNavigate} from 'react-router';

import {loadBundlesForPath} from '#lit/lib/bundle_loader.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import type {FlywheelRouter} from '#lit/lib/router.js';
import {useCurrentOrgReactPages} from '#queries/react_pages/index.js';
import type {MaybePromise} from '#types/promises.js';
import {findRegistryMatch} from '#ui/atoms/link/utils.js';

type Callable = (...args: unknown[]) => unknown;
type Loader<TArgs extends LoaderFunctionArgs, TResult> = (
  args: TArgs
) => MaybePromise<TResult>;

export function loadLitBundlesForRequest(request: Request): Promise<void> {
  return loadBundlesForPath(new URL(request.url).pathname);
}

export function withLitBundles<TArgs extends LoaderFunctionArgs, TResult>(
  loader: Loader<TArgs, TResult>
): Loader<TArgs, TResult> {
  return async args => {
    await loadLitBundlesForRequest(args.request);
    return await loader(args);
  };
}

/**
 * Intercepts `FlywheelComponent.router.navigate()` and delegates
 * it to React Router's `navigate()`
 **/
export function useFlywheelRouterProxy(): void {
  const navigate = useNavigate();
  const ctx = useContext(UNSAFE_DataRouterContext);
  const {data: reactPages} = useCurrentOrgReactPages();

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  const ctxRef = useRef(ctx);
  useEffect(() => {
    ctxRef.current = ctx;
  });

  const reactPagesRef = useRef(reactPages);
  useEffect(() => {
    reactPagesRef.current = reactPages;
  });

  useEffect(() => {
    const previousRouter: FlywheelRouter | null = FlywheelComponent.router;

    FlywheelComponent.router = new Proxy(
      previousRouter ?? ({} as FlywheelRouter),
      {
        get(obj, prop, receiver) {
          if (prop === 'navigate') {
            return (path: string, state: Record<string, unknown> = {}) => {
              const routes = ctxRef.current?.router.routes;
              const isReactRoute =
                routes !== null &&
                routes !== undefined &&
                matchRoutes(routes, path) !== null;
              const dualModePage = findRegistryMatch(
                reactPagesRef.current,
                path
              );
              const isDisabledDualMode =
                dualModePage !== undefined && !dualModePage.enabled;

              if (isReactRoute && !isDisabledDualMode) {
                navigateRef.current(path, {state});
                return;
              }

              previousRouter?.navigate(path, state);
            };
          }
          const value = Reflect.get(obj, prop, receiver);
          return typeof value === 'function'
            ? (value as Callable).bind(obj)
            : value;
        },
      }
    );

    return () => {
      FlywheelComponent.router = previousRouter;
    };
  }, []);
}
